import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

import { logout } from '../features/auth/authSlice';
import useSuperUserPermission from '../hooks/useSuperUserPermission';
import EventStream from '../components/EventStream';
import AIAgentDashboard from '../components/AIAgentDashboard';
import LlamaIndexDashboard from '../components/LlamaIndexDashboard';
import SpeechToTextDashboard from '../components/SpeechToTextDashboard';

const Dashboard = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canCreateSuperUser, loading } = useSuperUserPermission();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const navigateToGraph = () => {
    navigate('/graph');
  };

  const navigateToPrivilegedData = () => {
    navigate('/privileged-data');
  };

  const triggerTestEvent = async () => {
    if (!user || !user.uid) {
      console.error('User is not defined or missing UID');
      return;
    }
    console.log('Triggering test event -> user:', user);
    try {
      // const response = await axios.post('http://localhost:4010/test-event', { userId: user.uid });
      const response = await axios.post(process.env.REACT_APP_STREAM_EXTENDED_NEURO_URL + '/test-event', { userId: user.uid });
      console.log('Test event triggered:', response.data);
    } catch (error) {
      console.error('Error triggering test event:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }
    }
  };

  const handleClearEvents = () => {
    console.log('Clearing events');
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {false && <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <img className="h-8 w-auto" src="/logo.png" alt="Company Logo" />
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={handleLogout}
                className="ml-4 px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>}

      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg leading-6 font-medium text-gray-900">User Information</h2>
              <div className="mt-3 sm:mt-5">
                <p className="text-sm text-gray-500">Welcome back,</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">{user?.displayName || 'User'}</p>
                <p className="mt-1 text-sm text-gray-500">Email: {user?.email}</p>
                <p className="mt-1 text-sm text-gray-500">Role: <span className="font-medium text-indigo-600">{user?.role || 'Loading...'}</span></p>
              </div>
            </div>
            <div className="px-4 py-4 sm:px-6">
              <div className="flex space-x-3">
                <button
                  onClick={navigateToGraph}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  View Data Graph
                </button>
                <button
                  onClick={navigateToPrivilegedData}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  View Privileged Data
                </button>
                <Link
                  to="/financial"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Financial Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <EventStream />
            </div>
          </div>
        </div>

        {canCreateSuperUser && (
          <div className="mt-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">SuperUser Actions</h3>
                <div className="space-y-3">
                  <Link
                    to="/create-super-user"
                    className="block w-full text-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Create SuperUser
                  </Link>
                  <Link
                    to="/authenticate-super-user"
                    className="block w-full text-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Authenticate SuperUser
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Navigation</h3>
              <div className="space-y-3">
                <Link
                  to="/calendar"
                  className="block w-full text-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  View Calendar
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <AIAgentDashboard />
        </div>
        <div className="mb-8">
          <LlamaIndexDashboard />
        </div>
        <div className="mb-8">
          <SpeechToTextDashboard />
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
// b4dAppl3s!
// BenjiBooDooDoot3r!

